import React, { useState, useEffect, lazy, Suspense } from "react";
import { VEDIC_MANTRAS, HAWAN_STEPS } from "./data";
import { Mantra, UserSettings, PracticeSession, ReligionTab } from "./types";
import PitchVisualizer from "./components/PitchVisualizer";
import VocalWarmup from "./components/VocalWarmup";
import HawanGuide from "./components/HawanGuide";
import TarpanaGuide from "./components/TarpanaGuide";
import GuruChat from "./components/GuruChat";
import SettingsPanel from "./components/SettingsPanel";
import WebRTCChantingCircle from "./components/WebRTCChantingCircle";
import RitualRoadmap from "./components/RitualRoadmap";
import RitualAssistant from "./components/RitualAssistant";
import DailyRitualAffirmation from "./components/DailyRitualAffirmation";
import DailyRitualReminder from "./components/DailyRitualReminder";
import RitualGoalsTracker from "./components/RitualGoalsTracker";
import RitualAmbientSound from "./components/RitualAmbientSound";
import PranayamaBreathing from "./components/PranayamaBreathing";
import LegalMoRFooter from "./components/LegalMoRFooter";
import { CustomMantraModal } from "./components/CustomMantraModal";
import MantraMetadataAccordion from "./components/MantraMetadataAccordion";
import AIStreamDemo from "./components/AIStreamDemo";
import { StreamErrorBoundary } from "./components/StreamErrorBoundary";
import SvaraContourVisualizer from "./components/SvaraContourVisualizer";
import SyllableKaraokeAligner from "./components/SyllableKaraokeAligner";

// Code-split heavy non-critical components using React.lazy for optimized bundle size
const MultifaithRituals = lazy(() => import("./components/MultifaithRituals"));
const AstroScheduler = lazy(() => import("./components/AstroScheduler"));
const ProAudioStudio = lazy(() => import("./components/ProAudioStudio"));
const VedicAchievements = lazy(() => import("./components/VedicAchievements"));
const ProFeaturesHub = lazy(() => import("./components/ProFeaturesHub"));
const ProSpiritualDevotion = lazy(() => import("./components/ProSpiritualDevotion"));
const AstroHoraTimer = lazy(() => import("./components/AstroHoraTimer"));
const TmdHashInspector = lazy(() => import("./components/TmdHashInspector"));
const AutomataStudio = lazy(() => import("./components/AutomataStudio"));
const AncientRunesMonographModal = lazy(() => import("./components/AncientRunesMonographModal"));
const FiniteDimStudio = lazy(() => import("./components/FiniteDimStudio"));
const SpdeV6Studio = lazy(() => import("./components/SpdeV6Studio"));
import { checkStorageQuota } from "./lib/storage-quota";
import { exportHistoryToPDF } from "./utils/pdfExport";
import { sendEmailRPC } from "./lib/api";
import { Flame, Sparkles, Youtube, Globe, GraduationCap, History, PlusCircle, Check, Info, Droplet, Send, Mail, AlertCircle, FileDown, Clock, Sun, Moon, Lock, Zap, BookOpen, Music, Settings, Building2, HardDrive, Shield, Compass, Layers, Waves } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { APPLICATION_STAGE, CURRENT_CONFIG } from "./config";
import { pullFromD1 } from "./lib/d1-sync";
import { addPracticeSessionD1, clearHistoryD1 } from "./lib/d1-client";
import { getAuthSession, saveAuthSession, clearAuthSession, getAuthSessionAsync, saveAuthSessionAsync } from "./lib/authSession";
import { createSignedBackup, aghoraBitwiseHash64 } from "@sovereign/auth";

export default function App() {
  // Master Theme State (toggles between default dark and light-ritual mode)
  const [theme, setTheme] = useState<"dark" | "light-ritual">(() => {
    try {
      const savedTheme = localStorage.getItem("vedic_manual_theme");
      return (savedTheme as "dark" | "light-ritual") || "dark";
    } catch (e) {
      return "dark";
    }
  });

  useEffect(() => {
    localStorage.setItem("vedic_manual_theme", theme);
    const root = document.documentElement;
    if (theme === "light-ritual") {
      root.classList.add("light-ritual");
    } else {
      root.classList.remove("light-ritual");
    }
  }, [theme]);

  // Master Mantra States (loads seeded and user-generated mantras)
  const [mantras, setMantras] = useState<Mantra[]>([]);
  const [selectedMantra, setSelectedMantra] = useState<Mantra | null>(null);
  const [activeRitual, setActiveRitual] = useState<"hawan" | "tarpana" | "assistant" | "scheduler">("hawan");
  const [activeReligion, setActiveReligion] = useState<ReligionTab>("hinduism");
  const [isProHubOpen, setIsProHubOpen] = useState<boolean>(false);
  const [isTmdModalOpen, setIsTmdModalOpen] = useState<boolean>(false);
  const [isAutomataModalOpen, setIsAutomataModalOpen] = useState<boolean>(false);
  const [isMonographModalOpen, setIsMonographModalOpen] = useState<boolean>(false);
  const [isFiniteDimModalOpen, setIsFiniteDimModalOpen] = useState<boolean>(false);
  const [isSpdeV6ModalOpen, setIsSpdeV6ModalOpen] = useState<boolean>(false);
  const [lowStorageWarning, setLowStorageWarning] = useState<{ isLow: boolean; minutes: number } | null>(null);

  // Live pitch telemetry from Audio DSP engine
  const [livePitch, setLivePitch] = useState<number | null>(null);
  const [liveAccent, setLiveAccent] = useState<string>("SAMANYA");
  const [liveCentDeviation, setLiveCentDeviation] = useState<number>(0);

  useEffect(() => {
    const handlePitchMetric = (e: any) => {
      if (e.detail) {
        setLivePitch(e.detail.pitch || null);
        setLiveAccent(e.detail.accent || "SAMANYA");
        setLiveCentDeviation(typeof e.detail.centDeviation === "number" ? e.detail.centDeviation : 0);
      }
    };
    window.addEventListener("pitch-metric", handlePitchMetric);
    return () => window.removeEventListener("pitch-metric", handlePitchMetric);
  }, []);

  useEffect(() => {
    checkStorageQuota().then((res) => {
      if (res.isLowStorage) {
        setLowStorageWarning({ isLow: true, minutes: res.estimatedRecordingMinutes });
      }
    });
  }, []);

  // Multi-tenant subdomain router state
  const [activeSubdomain, setActiveSubdomain] = useState<string>("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname.toLowerCase();
      const pathname = window.location.pathname.toLowerCase();
      let detected = "";
      try {
        const params = new URLSearchParams(window.location.search);
        detected = params.get("subdomain") || params.get("app") || params.get("tab") || params.get("ritual") || "";
      } catch (e) {
        // fallback
      }

      // Check path-based routes for monolithic site integration (e.g. /rituals/hawan, /hawan, /hora)
      if (!detected && pathname) {
        const pathSegments = pathname.split("/").filter(Boolean);
        const match = pathSegments.find(p => [
          "hawan", "tarpana", "scheduler", "assistant", "guru", 
          "phonetics", "warmup", "calibration", "sync", "hora"
        ].includes(p));
        if (match) {
          detected = match;
        }
      }

      if (!detected) {
        if (hostname.includes("bhutadamarasena.com")) {
          const parts = hostname.split(".");
          if (parts.length >= 3 && parts[0] !== "www" && parts[0] !== "app") {
            detected = parts[0];
          }
        } else {
          // Fallback parsing for staging or localhost
          const parts = hostname.split(".");
          if (parts.length >= 2 && parts[0] !== "localhost" && parts[0] !== "www" && parts[0] !== "app" && !parts[0].includes("run.app")) {
            detected = parts[0];
          }
        }
      }

      if (detected) {
        const validSubdomains = [
          "ancient-runes", "hawan", "tarpana", "scheduler", "assistant", "guru", 
          "phonetics", "warmup", "calibration", "hora"
        ];
        if (validSubdomains.includes(detected)) {
          setActiveSubdomain(detected);
          
          // Apply routing focus shifts matching core Vedic Phonetics and Ritual Suite modules
          if (detected === "hawan") {
            setActiveRitual("hawan");
          } else if (detected === "tarpana") {
            setActiveRitual("tarpana");
          } else if (detected === "scheduler" || detected === "hora") {
            setActiveRitual("scheduler");
          } else if (detected === "assistant" || detected === "guru") {
            setActiveRitual("assistant");
          } else if (detected === "warmup" || detected === "phonetics") {
            setShowWarmup(true);
          }
        }
      }
    }
  }, []);

  // Category Hub Tab Navigation State
  type MainTab = "chanting" | "rituals" | "practice" | "tracker" | "runes" | "aistream" | "settings";
  const [mainTab, setMainTab] = useState<MainTab>("chanting");

  // Native Email state variables for Cloudflare Email Engine outbound reporting (support@bhutadamarasena.com)
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLockedFeatureModal, setShowLockedFeatureModal] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  // User Settings State
  const [settings, setSettings] = useState<UserSettings>({
    pitchProfile: "medium-male",
    showDevanagari: true,
    showTransliteration: true,
    autoScroll: true
  });

  // Unified Global Subscription Tier States (Self-MoR via Stripe)
  const [activeTier, setActiveTier] = useState<"PRO" | "LIFETIME_FOUNDER" | "GENERAL">("GENERAL");
  const [activeTerritory, setActiveTerritory] = useState("");

  // Dynamic evaluation of subscription tier via shared cookies & scoped JWTs
  useEffect(() => {
    const evaluateTier = async () => {
      // 1. Check shared JWT session under bhutadamarasena.com
      let session = getAuthSession();
      if (!session) {
        session = await getAuthSessionAsync();
      }

      if (session) {
        if (session.tier === "PRO" || session.tier === "LIFETIME_FOUNDER") {
          setActiveTier(session.tier as any);
          setActiveTerritory("");
          return;
        }
      }

      // 2. If explicit PRO setting is saved
      if (settings.subscriptionTier === "PRO") {
        setActiveTier("PRO");
        setActiveTerritory("");
        await saveAuthSessionAsync({ tier: "PRO" });
        return;
      }

      // 3. Fallback to general/free tier
      setActiveTier("GENERAL");
      setActiveTerritory("");
    };

    evaluateTier();
    
    // Listen for storage events and session changes
    window.addEventListener("storage", evaluateTier);
    window.addEventListener("vedic_session_changed", evaluateTier);
    // Periodic background sync (30s) alongside instant reactive event listeners
    const interval = setInterval(evaluateTier, 30000);
    return () => {
      window.removeEventListener("storage", evaluateTier);
      window.removeEventListener("vedic_session_changed", evaluateTier);
      clearInterval(interval);
    };
  }, [settings.subscriptionTier]);

  // Practice History State
  const [history, setHistory] = useState<PracticeSession[]>([]);

  // Custom Mantra Modal/Form State
  const [showWarmup, setShowWarmup] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Load state from LocalStorage & Cloudflare D1 SQL on mount
  useEffect(() => {
    const initializeState = async () => {
      // First attempt to pull the latest state from Cloudflare D1 SQL database
      try {
        await pullFromD1();
      } catch (err) {
        console.warn("Cloudflare D1 initialization bypassed. Using local cache fallback.", err);
      }

      // Mantras
      const savedMantras = localStorage.getItem("vedic_mantras_store");
      if (savedMantras) {
        try {
          const parsed = JSON.parse(savedMantras);
          setMantras(parsed);
          setSelectedMantra(parsed[0] || VEDIC_MANTRAS[0]);
        } catch (e) {
          setMantras(VEDIC_MANTRAS);
          setSelectedMantra(VEDIC_MANTRAS[0]);
        }
      } else {
        setMantras(VEDIC_MANTRAS);
        setSelectedMantra(VEDIC_MANTRAS[0]);
        localStorage.setItem("vedic_mantras_store", JSON.stringify(VEDIC_MANTRAS));
      }

      // Settings
      const savedSettings = localStorage.getItem("vedic_settings");
      if (savedSettings) {
        try {
          setSettings(JSON.parse(savedSettings));
        } catch (e) {}
      }

      // History
      const savedHistory = localStorage.getItem("vedic_history");
      if (savedHistory) {
        try {
          setHistory(JSON.parse(savedHistory));
        } catch (e) {}
      }
    };

    initializeState();
  }, []);

  // Listen for external history updates (e.g., from RitualAssistant)
  useEffect(() => {
    const handleHistorySync = () => {
      const savedHistory = localStorage.getItem("vedic_history");
      if (savedHistory) {
        try {
          setHistory(JSON.parse(savedHistory));
        } catch (e) {}
      } else {
        setHistory([]);
      }
    };
    window.addEventListener("vedic-history-updated", handleHistorySync);
    return () => window.removeEventListener("vedic-history-updated", handleHistorySync);
  }, []);

  // Listen for external settings decryption/updates
  useEffect(() => {
    const handleSettingsSync = () => {
      const savedSettings = localStorage.getItem("vedic_settings");
      if (savedSettings) {
        try {
          setSettings(JSON.parse(savedSettings));
        } catch (e) {}
      }
    };
    window.addEventListener("vedic-settings-updated", handleSettingsSync);
    return () => window.removeEventListener("vedic-settings-updated", handleSettingsSync);
  }, []);

  // Save changes helper functions
  const saveSettings = (nextSettings: UserSettings) => {
    setSettings(nextSettings);
    localStorage.setItem("vedic_settings", JSON.stringify(nextSettings));
  };

  const addPracticeSession = async (score: number, matchPercent: number) => {
    if (!selectedMantra) return;
    const canonicalTimestamp = new Date().toISOString();
    const session: PracticeSession = {
      id: "session_" + Date.now(),
      date: canonicalTimestamp,
      mantraId: selectedMantra.id,
      mantraTitle: selectedMantra.title,
      score,
      pitchMatches: matchPercent,
      durationSeconds: 15,
      notes: score >= 80 ? "Svara Pitch aligned" : "Phonetics require steady practice"
    };

    // Cryptographically sign practice session data before saving to local storage & D1
    const signedSession = await createSignedBackup(session);
    const aghHash = aghoraBitwiseHash64(`${session.id}:${session.mantraId}:${score}:${matchPercent}:${session.date}`);
    const sessionToSave: PracticeSession = {
      ...session,
      notes: session.notes + ` [AGH64: ${aghHash.substring(0, 8)}]`
    };

    const updatedHistory = [sessionToSave, ...history];
    setHistory(updatedHistory);
    try {
      localStorage.setItem("vedic_history", JSON.stringify(updatedHistory));
      localStorage.setItem("vedic_last_signed_session", JSON.stringify(signedSession));
    } catch (e) {
      console.warn("Local storage write failed:", e);
    }
    await addPracticeSessionD1(sessionToSave);
  };

  const clearHistory = async () => {
    setHistory([]);
    await clearHistoryD1();
  };

  const handleExportPDF = () => {
    exportHistoryToPDF(history);
  };

  const handleSendEmailReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientEmail) return;
    setEmailStatus("sending");
    setEmailError("");

    try {
      const logsHtml = history.map(h => `
        <tr style="border-bottom: 1px solid #262626;">
          <td style="padding: 12px; font-weight: bold; color: #f59e0b;">${h.mantraTitle}</td>
          <td style="padding: 12px; color: #e5e5e5;">${h.date}</td>
          <td style="padding: 12px; font-weight: bold; color: ${h.score >= 80 ? '#10b981' : h.score >= 60 ? '#f59e0b' : '#ef4444'};">${h.score}%</td>
          <td style="padding: 12px; color: #a3a3a3; font-style: italic;">${h.notes}</td>
        </tr>
      `).join("");

      const htmlText = `
        <div style="background-color: #0a0a0a; color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 32px; border-radius: 16px; border: 1px solid #262626; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #f97316; border-bottom: 1px solid #262626; padding-bottom: 16px; margin-top: 0;">Vedic Phonetic Practice Report</h2>
          <p style="font-size: 14px; color: #a3a3a3; line-height: 1.5;">Here is your personalized Sanskrit chant articulation summary compiled by the <strong>Vedic Manual Engine</strong>.</p>
          <table style="width: 100%; border-collapse: collapse; margin-top: 24px; font-size: 13px; text-align: left;">
            <thead>
              <tr style="background-color: #171717; border-bottom: 2px solid #262626; color: #a3a3a3;">
                <th style="padding: 12px;">Mantra Focus</th>
                <th style="padding: 12px;">Date</th>
                <th style="padding: 12px;">Accuracy</th>
                <th style="padding: 12px;">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${logsHtml}
            </tbody>
          </table>
          <p style="margin-top: 32px; font-size: 11px; color: #737373; border-top: 1px solid #262626; padding-top: 16px; line-height: 1.5;">
            Sent automatically from bhutadamarasena.com (BhutaDamaraSena R&D Labs).
          </p>
        </div>
      `;

      await sendEmailRPC(
        recipientEmail,
        "Your Vedic Phonetic Practice Report",
        htmlText
      );

      setEmailStatus("success");
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailStatus("idle");
        setRecipientEmail("");
      }, 2000);
    } catch (err: any) {
      setEmailStatus("error");
      setEmailError(err.message || "Could not complete the request.");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col antialiased selection:bg-amber-500 selection:text-neutral-950 font-sans">
      {/* Decorative background aura */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] rounded-full bg-orange-500/5 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40rem] h-[40rem] rounded-full bg-amber-500/5 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-neutral-800 bg-neutral-950/80 backdrop-blur-md px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-orange-500 to-amber-500 flex items-center justify-center text-neutral-950 shadow-md">
            <Flame className="w-6 h-6 fill-neutral-950" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-neutral-50 bg-clip-text">
                Ancient Runes
              </h1>
              <span className="text-[10px] font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/20 rounded-full px-2 py-0.5">
                v2.0.0
              </span>
            </div>
            <p className="text-xs text-neutral-400">
              Vedic, Avestan, Norse & Roman chanting matrix & pitch engine &bull; BhutaDamaraSena R&D Labs &bull; Preprint: <a href="https://doi.org/10.5281/zenodo.23361838" target="_blank" rel="noreferrer" className="text-amber-400 font-mono hover:underline">10.5281/zenodo.23361838</a>
            </p>
          </div>
        </div>

        {/* Header Navigation Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Ancient Runes Pro Engine Hub Button */}
          <button
            onClick={() => setIsProHubOpen(true)}
            className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/10 border border-amber-500/40 hover:border-amber-400 px-3 py-1.5 rounded-xl text-amber-300 hover:text-amber-100 transition cursor-pointer select-none font-semibold text-xs shadow-md shadow-amber-950/20"
            title="Ancient Runes Pro (Stripe Self-MoR)"
          >
            <Zap className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span>Ancient Runes Pro</span>
          </button>

          {/* Ancient Runes Zenodo Monograph & Software Button */}
          <button
            onClick={() => setIsMonographModalOpen(true)}
            className="flex items-center gap-1.5 bg-gradient-to-r from-amber-950/70 to-cyan-950/70 border border-amber-500/50 hover:border-amber-400 px-3 py-1.5 rounded-xl text-amber-200 hover:text-amber-100 transition cursor-pointer select-none font-semibold text-xs shadow-md"
            title="Ancient Runes: Zenodo Monograph Preprint (DOI: 10.5281/zenodo.23361838) & Software (DOI: 10.5281/zenodo.23391403)"
          >
            <BookOpen className="w-3.5 h-3.5 text-amber-400" />
            <span>Monograph & SW (Zenodo)</span>
          </button>

          {/* TMD-Hash v12.0 Cryptographic Oracle Button */}
          <button
            onClick={() => setIsTmdModalOpen(true)}
            className="flex items-center gap-1.5 bg-cyan-950/60 border border-cyan-500/40 hover:border-cyan-400 px-3 py-1.5 rounded-xl text-cyan-300 hover:text-cyan-100 transition cursor-pointer select-none font-semibold text-xs shadow-md"
            title="TMD-Hash v12.0 Cryptographic Oracle (Zenodo DOI: 10.5281/zenodo.23218553)"
          >
            <Shield className="w-3.5 h-3.5 text-cyan-400" />
            <span>TMD-12 Oracle (DOI)</span>
          </button>

          {/* Automata Version 10.0 Studio Button */}
          <button
            onClick={() => setIsAutomataModalOpen(true)}
            className="flex items-center gap-1.5 bg-indigo-950/60 border border-indigo-500/40 hover:border-indigo-400 px-3 py-1.5 rounded-xl text-indigo-300 hover:text-indigo-100 transition cursor-pointer select-none font-semibold text-xs shadow-md"
            title="Automata Version 10.0 Trajectory Optimization (Zenodo DOI: 10.5281/zenodo.23256017)"
          >
            <Compass className="w-3.5 h-3.5 text-indigo-400" />
            <span>Automata V10.0 (DOI)</span>
          </button>

          {/* Finite-Dimensional Modular Theory Version 10.0 Studio Button */}
          <button
            onClick={() => setIsFiniteDimModalOpen(true)}
            className="flex items-center gap-1.5 bg-violet-950/60 border border-violet-500/40 hover:border-violet-400 px-3 py-1.5 rounded-xl text-violet-300 hover:text-violet-100 transition cursor-pointer select-none font-semibold text-xs shadow-md"
            title="Finite-Dimensional Modular Theory V10.0 (Zenodo DOI: 10.5281/zenodo.23237005)"
          >
            <Layers className="w-3.5 h-3.5 text-violet-400" />
            <span>Finite-Dim V10 (DOI)</span>
          </button>

          {/* Coupled SPDE Version 6.0 Studio Button */}
          <button
            onClick={() => setIsSpdeV6ModalOpen(true)}
            className="flex items-center gap-1.5 bg-cyan-950/60 border border-cyan-500/40 hover:border-cyan-400 px-3 py-1.5 rounded-xl text-cyan-300 hover:text-cyan-100 transition cursor-pointer select-none font-semibold text-xs shadow-md"
            title="Coupled SPDE Version 6.0 Grand Synthesis (Zenodo DOI: 10.5281/zenodo.23280282)"
          >
            <Waves className="w-3.5 h-3.5 text-cyan-400" />
            <span>SPDE V6.0 (DOI)</span>
          </button>

          {/* Ambient Soundscape Controller */}
          <RitualAmbientSound activeReligion={activeReligion} />

          {/* Light/Ritual Mode Global Theme Toggle */}
          <button
            onClick={() => setTheme(theme === "dark" ? "light-ritual" : "dark")}
            className="flex items-center gap-1.5 bg-neutral-900 border border-neutral-800 hover:border-amber-500/40 px-3 py-1.5 rounded-lg text-neutral-400 hover:text-amber-500 transition cursor-pointer select-none"
            title={theme === "dark" ? "Switch to Light/Ritual Mode (Daytime Practice)" : "Switch to Cosmic Dark Mode (Nighttime Practice)"}
          >
            {theme === "dark" ? (
              <>
                <Sun className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs font-medium">Ritual Light</span>
              </>
            ) : (
              <>
                <Moon className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs font-medium">Cosmic Dark</span>
              </>
            )}
          </button>

          <a
            href="https://bhutadamarasena.com"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-neutral-900 border border-neutral-800 px-3 py-1.5 rounded-lg text-neutral-400 hover:text-amber-400 transition"
          >
            <Globe className="w-3.5 h-3.5" />
            bhutadamarasena.com
          </a>

          <a
            href="https://youtube.com/@bhutadamarasena"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-neutral-900 border border-neutral-800 px-3 py-1.5 rounded-lg text-neutral-400 hover:text-red-400 transition"
          >
            <Youtube className="w-3.5 h-3.5" />
            @bhutadamarasena
          </a>
        </div>
      </header>

      {/* Main Content Dashboard */}
      <main className="relative z-10 flex-1 flex flex-col gap-6 p-6 max-w-7xl mx-auto w-full">
        
        {/* Low Storage Quota Alert Banner */}
        <AnimatePresence>
          {lowStorageWarning && lowStorageWarning.isLow && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-3.5 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-200 flex items-center justify-between gap-3 shadow-lg"
            >
              <div className="flex items-center space-x-3">
                <HardDrive className="w-5 h-5 text-amber-400 animate-pulse" />
                <div>
                  <h4 className="font-bold text-xs text-amber-100 flex items-center gap-1.5">
                    <span>LOW DISK STORAGE WARNING FOR OFFLINE RECORDINGS</span>
                    <span className="text-[10px] bg-amber-500 text-slate-950 font-extrabold px-2 py-0.5 rounded-full">
                      ~{lowStorageWarning.minutes} mins remaining
                    </span>
                  </h4>
                  <p className="text-[11px] text-amber-200/90">
                    Device local storage is running low. Please sync or export existing chant recordings to avoid losing session data during offline Hawan practices.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setLowStorageWarning(null)}
                className="px-3 py-1 bg-amber-500 text-slate-950 font-bold text-xs rounded-lg hover:bg-amber-400 transition-colors whitespace-nowrap"
              >
                Dismiss
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Containerized Dashboard Hub Navigation Bar */}
        <div className="bg-neutral-900/95 border border-neutral-800 rounded-2xl p-2 flex flex-wrap items-center justify-between gap-2 shadow-2xl backdrop-blur-md sticky top-4 z-40">
          <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
            <button
              onClick={() => setMainTab("chanting")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "chanting"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Music className="w-4 h-4" />
              <span>Chanting & Pitch</span>
            </button>

            <button
              onClick={() => setMainTab("rituals")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "rituals"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Flame className="w-4 h-4" />
              <span>Sacred Rituals</span>
            </button>

            <button
              onClick={() => setMainTab("practice")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "practice"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span>Mind & Audio Studio</span>
            </button>

            <button
              onClick={() => setMainTab("tracker")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "tracker"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Goals & Calendar</span>
            </button>

            <button
              onClick={() => setMainTab("runes")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "runes"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Ancient Runes Matrix</span>
            </button>

            <button
              onClick={() => setMainTab("aistream")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "aistream"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Zap className="w-4 h-4 text-amber-400" />
              <span>AI Edge Stream</span>
            </button>

            <button
              onClick={() => setMainTab("settings")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                mainTab === "settings"
                  ? "bg-amber-500 text-neutral-950 shadow-md shadow-amber-500/20"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800/60"
              }`}
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
          </div>
        </div>
        
        <Suspense fallback={
          <div className="p-12 bg-neutral-900/60 border border-neutral-800 rounded-2xl text-center text-xs font-mono text-amber-400/80 animate-pulse flex flex-col items-center justify-center gap-3">
            <Sparkles className="w-6 h-6 text-amber-500 animate-spin" />
            <span>Harmonizing Sacred Vedic Module...</span>
          </div>
        }>
          {/* TAB 1: CHANTING & PITCH */}
          {mainTab === "chanting" && (
            <div className="flex flex-col gap-6">
              {/* Active Mantra Selector & Sanskrit Text Display */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">
                      Articulating Mantra Focus
                    </h3>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={selectedMantra?.id || ""}
                      onChange={(e) => {
                        const m = mantras.find(item => item.id === e.target.value);
                        if (m) setSelectedMantra(m);
                      }}
                      className="bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500 transition-colors cursor-pointer"
                    >
                      {mantras.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => setShowAddModal(true)}
                      className="flex items-center gap-1.5 text-xs bg-amber-500 text-neutral-950 font-semibold px-3 py-2 rounded-lg hover:bg-amber-600 transition"
                    >
                      <PlusCircle className="w-3.5 h-3.5" />
                      Add Mantra
                    </button>
                  </div>
                </div>

                {selectedMantra && (
                  <div className="flex flex-col gap-4">
                    <div className="bg-neutral-950 border border-neutral-800/80 rounded-xl p-5 relative overflow-hidden group">
                      {settings.showDevanagari && (
                        <p className="text-xl md:text-2xl font-bold text-center text-amber-150 leading-relaxed tracking-wide select-all mb-2">
                          {selectedMantra.sanskrit}
                        </p>
                      )}
                      {settings.showTransliteration && (
                        <p className="text-xs italic text-center text-neutral-400 font-mono select-all">
                          {selectedMantra.iast}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-neutral-950/40 p-4 border border-neutral-800/40 rounded-xl">
                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1 block">
                          TRANSLATION
                        </span>
                        <p className="text-xs text-neutral-300 leading-relaxed">
                          {selectedMantra.translation}
                        </p>
                      </div>

                      <div className="bg-neutral-950/40 p-4 border border-neutral-800/40 rounded-xl">
                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1 block">
                          SACRED SIGNIFICANCE
                        </span>
                        <p className="text-xs text-neutral-300 leading-relaxed">
                          {selectedMantra.significance}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Accordion-style reveal pattern for Mantra Metadata & Context */}
              {selectedMantra && (
                <MantraMetadataAccordion
                  mantra={selectedMantra}
                  allMantras={mantras}
                  onSelectMantra={(m) => setSelectedMantra(m)}
                />
              )}

              {/* Vocal Warmup */}
              {selectedMantra && (
                <div>
                  <button
                    onClick={() => setShowWarmup(!showWarmup)}
                    className="w-full bg-neutral-900 border border-neutral-800 hover:border-orange-500/40 p-4 rounded-xl flex items-center justify-between transition group cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-500/10 text-orange-400 p-2 rounded-lg group-hover:scale-105 transition-transform">
                        <Flame className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                          Vocal Warmup (Svara Purvanga)
                        </h4>
                        <p className="text-[11px] text-neutral-500 mt-0.5">
                          Prepare your vocal resonance for Sanskrit phonetics (Anudatta, Svarita, Udatta).
                        </p>
                      </div>
                    </div>
                    <div className="text-[11px] font-bold text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded border border-orange-500/20">
                      {showWarmup ? "Collapse Guide" : "Open Warmup Guide"}
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {showWarmup && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden mt-3"
                      >
                        <VocalWarmup
                          pitchProfile={settings.pitchProfile}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Live Syllable Karaoke Aligner */}
              {selectedMantra && selectedMantra.phoneticSegments && (
                <SyllableKaraokeAligner
                  segments={selectedMantra.phoneticSegments}
                  currentPitchHz={livePitch}
                  targetPitchHz={220}
                />
              )}

              {/* Live Vedic Phonetics Svara Contour Visualizer */}
              {selectedMantra && (
                <SvaraContourVisualizer
                  currentPitchHz={livePitch || 220}
                  clarity={livePitch ? 0.95 : 0.7}
                  svaraAccent={liveAccent as "UDATTA" | "ANUDATTA" | "SVARITA" | "SAMANYA"}
                  centDeviation={liveCentDeviation}
                  targetBasePitchHz={220}
                />
              )}

              {/* Pitch Tracker & Vocal Analyzer */}
              {selectedMantra && (
                <PitchVisualizer
                  activeMantra={selectedMantra}
                  onSessionComplete={(score, matches) => {
                    addPracticeSession(score, matches);
                  }}
                />
              )}


            {/* Practice History */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl" id="practice-logs">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-3 mb-4">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-500" />
                  <h4 className="text-sm font-semibold text-neutral-200">Practice Logs</h4>
                </div>
                <div className="flex items-center gap-2.5">
                  {history.length > 0 && (
                    <button
                      onClick={() => {
                        if (activeTier === "GENERAL") {
                          setShowLockedFeatureModal("PDF Report Export");
                        } else {
                          handleExportPDF();
                        }
                      }}
                      className={`text-[10px] font-semibold rounded-lg px-2.5 py-1 transition flex items-center gap-1 cursor-pointer ${
                        activeTier === "GENERAL"
                          ? "text-neutral-500 bg-neutral-800/40 border border-neutral-800"
                          : "text-orange-400 hover:text-orange-300 bg-orange-500/10 hover:bg-orange-500/15 border border-orange-500/20"
                      }`}
                    >
                      {activeTier === "GENERAL" ? <Lock className="w-3 h-3 text-amber-500" /> : <FileDown className="w-3 h-3" />}
                      Export PDF
                    </button>
                  )}
                  {history.length > 0 && (
                    <button
                      onClick={() => {
                        if (activeTier === "GENERAL") {
                          setShowLockedFeatureModal("Email Report Distribution");
                        } else {
                          setShowEmailModal(true);
                          setEmailStatus("idle");
                          setEmailError("");
                        }
                      }}
                      className={`text-[10px] font-semibold rounded-lg px-2 py-1 transition flex items-center gap-1 cursor-pointer ${
                        activeTier === "GENERAL"
                          ? "text-neutral-500 bg-neutral-800/40 border border-neutral-800"
                          : "text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20"
                      }`}
                    >
                      {activeTier === "GENERAL" ? <Lock className="w-3 h-3 text-amber-500" /> : <Mail className="w-3 h-3" />}
                      Email Report
                    </button>
                  )}
                  {history.length > 0 && (
                    <button
                      onClick={clearHistory}
                      className="text-[10px] text-neutral-500 hover:text-neutral-300 font-medium cursor-pointer"
                    >
                      Clear Logs
                    </button>
                  )}
                </div>
              </div>

              {history.length === 0 ? (
                <div className="text-center py-6 text-neutral-500 text-xs">
                  No chant recordings yet. Complete a Practice session above to save logs.
                </div>
              ) : (
                <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                  {history.map((h) => (
                    <div key={h.id} className="bg-neutral-950 border border-neutral-800/60 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <div className="text-xs font-semibold text-neutral-200 truncate max-w-[200px]">
                          {h.mantraTitle}
                        </div>
                        <div className="text-[10px] text-neutral-500 flex items-center gap-1.5 mt-0.5">
                          <span>{h.date}</span>
                          <span>•</span>
                          <span className="text-amber-500/80">{h.notes}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-xs font-mono font-bold ${
                          h.score >= 80 ? "text-emerald-400" : h.score >= 60 ? "text-amber-400" : "text-red-400"
                        }`}>
                          {h.score}% Match
                        </span>
                        <span className="text-[9px] text-neutral-500">
                          Accuracy
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: SACRED RITUALS */}
        {mainTab === "rituals" && (
          <div className="flex flex-col gap-6">
            {/* Astro-Hora Real-time Muhurta Timer Widget */}
            <AstroHoraTimer />

            {/* Universal Faith & Tradition Selector & Mini-Tab Container */}
            <MultifaithRituals
              activeReligion={activeReligion}
              onSelectReligion={(rel) => setActiveReligion(rel)}
              renderHawanGuide={
                <div className="space-y-6">
                  <HawanGuide
                    onMantraSelect={(m) => setSelectedMantra(m)}
                    activeMantraId={selectedMantra?.id || ""}
                  />
                </div>
              }
              renderTarpanaGuide={
                activeTier === "GENERAL" ? (
                  <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-8 shadow-xl flex flex-col items-center text-center justify-center min-h-[300px]">
                    <div className="w-12 h-12 bg-sky-500/10 border border-sky-500/20 rounded-full flex items-center justify-center text-sky-400 mb-4 animate-pulse">
                      <Droplet className="w-5 h-5 fill-current" />
                    </div>
                    <div className="flex items-center gap-1 mb-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      <Lock className="w-2.5 h-2.5" /> Premium Ritual Guide
                    </div>
                    <h4 className="text-sm font-bold text-neutral-200 mb-1">Water Tarpana Ritual Wizard</h4>
                    <p className="text-[11px] text-neutral-400 max-w-xs mb-5 leading-relaxed">
                      Our expert ancestral libations guide requires complex steps, tarpan mantras, and alignment.
                    </p>
                    <button onClick={() => setIsProHubOpen(true)} className="px-3.5 py-1.5 bg-amber-500 text-neutral-950 font-bold rounded-lg text-[10px] uppercase font-mono tracking-wider cursor-pointer">
                      Get Pro
                    </button>
                  </div>
                ) : (
                  <TarpanaGuide
                    onMantraSelect={(m) => setSelectedMantra(m as any)}
                    activeMantraId={selectedMantra?.id || ""}
                  />
                )
              }
              renderAstroScheduler={
                activeTier === "GENERAL" ? (
                  <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-8 shadow-xl flex flex-col items-center text-center justify-center min-h-[300px]">
                    <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/20 rounded-full flex items-center justify-center text-amber-500 mb-4 animate-pulse">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-1 mb-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      <Lock className="w-2.5 h-2.5" /> Premium Ritual Guide
                    </div>
                    <h4 className="text-sm font-bold text-neutral-200 mb-1">Astro-Hora Muhūrta Scheduler</h4>
                    <p className="text-[11px] text-neutral-400 max-w-xs mb-5 leading-relaxed">
                      Plan daily pujas and hawans with dynamic astrolocal transits.
                    </p>
                    <button onClick={() => setIsProHubOpen(true)} className="px-3.5 py-1.5 bg-amber-500 text-neutral-950 font-bold rounded-lg text-[10px] uppercase font-mono tracking-wider cursor-pointer">
                      Get Pro
                    </button>
                  </div>
                ) : (
                  <AstroScheduler activeReligion={activeReligion} />
                )
              }
              renderAghoraAssistant={
                activeTier === "GENERAL" ? (
                  <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-8 shadow-xl flex flex-col items-center text-center justify-center min-h-[300px]">
                    <div className="w-12 h-12 bg-orange-500/10 border border-orange-500/20 rounded-full flex items-center justify-center text-orange-400 mb-4 animate-pulse">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-1 mb-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      <Lock className="w-2.5 h-2.5" /> Premium Ritual Guide
                    </div>
                    <h4 className="text-sm font-bold text-neutral-200 mb-1">Guru Ritual Assistant</h4>
                    <p className="text-[11px] text-neutral-400 max-w-xs mb-5 leading-relaxed">
                      Access advanced sacrificial checklists and custom homa step-builders.
                    </p>
                    <button onClick={() => setIsProHubOpen(true)} className="px-3.5 py-1.5 bg-amber-500 text-neutral-950 font-bold rounded-lg text-[10px] uppercase font-mono tracking-wider cursor-pointer">
                      Get Pro
                    </button>
                  </div>
                ) : (
                  <RitualAssistant />
                )
              }
            />
          </div>
        )}

        {/* TAB 2: MIND & PRACTICE */}
        {mainTab === "practice" && (
          <div className="flex flex-col gap-6">
            {/* Pranayama & Breathwork Preparation Module */}
            <PranayamaBreathing activeReligion={activeReligion} />

            {/* Pro Spiritual Devotion, Japa Mala & Sacred Discipline Suite */}
            <ProSpiritualDevotion
              isProUser={activeTier === "PRO"}
              onOpenProModal={() => setIsProHubOpen(true)}
            />

            {/* Full-width Peer-to-Peer Chanting Circle */}
            <WebRTCChantingCircle />

            {/* AI Gurukul Chatbot */}
            <GuruChat
              activeTier={activeTier}
            />
          </div>
        )}

        {/* TAB 3: GOALS & CALENDAR */}
        {mainTab === "tracker" && (
          <div className="flex flex-col gap-6">
            {/* Daily Ritual Affirmation Quote Box */}
            <DailyRitualAffirmation activeReligion={activeReligion} />

            {/* Daily Ritual Reminder Chime & Web Notifications */}
            <DailyRitualReminder activeReligion={activeReligion} />

            {/* Ritual Goals & Daily Streak Target Tracker */}
            <RitualGoalsTracker activeReligion={activeReligion} />

            {/* Vedic Achievements & Spiritual Milestones */}
            <VedicAchievements history={history} activeTier={activeTier} />
          </div>
        )}

        {/* TAB 4: ANCIENT RUNES & EPIGRAPHY ROADMAP */}
        {mainTab === "runes" && (
          <div className="flex flex-col gap-6">
            {/* Full-width Ancient Runes & Ritual Roadmap Panel */}
            <RitualRoadmap />

            {/* Conditional Academic Research & Corporate Licensing Layer */}
            {(CURRENT_CONFIG.hasAcademicLayer || CURRENT_CONFIG.hasLicensingBlock) && (
              <section className="space-y-4" id="academic-corporate-layer">
                <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />
                  
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-neutral-800 pb-4 mb-4">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-amber-500" />
                      <div>
                        <h3 className="text-sm font-bold text-neutral-200 uppercase tracking-wider font-mono">
                          Research & Publications
                        </h3>
                        <p className="text-[10px] text-neutral-500">Corporate & Academic Peer-Validation Module</p>
                      </div>
                    </div>
                    <div className="text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-400 font-bold px-2.5 py-1 rounded">
                      {APPLICATION_STAGE} Mode
                    </div>
                  </div>

                  {CURRENT_CONFIG.hasAcademicLayer && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-neutral-950 border border-neutral-800/60 rounded-xl p-4">
                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider block mb-1">
                          Author & Affiliation
                        </span>
                        <p className="text-xs font-semibold text-neutral-200">BhutaDamaraSena R&D Labs</p>
                        <p className="text-[11px] text-amber-500/80 mt-0.5">Affiliation: {CURRENT_CONFIG.emailSignatureOrg || "Aghora Abraham Global LLC"}</p>
                        <div className="mt-3 pt-3 border-t border-neutral-800/40 flex items-center gap-2">
                          <span className="text-[10px] font-bold font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5">ORCID iD</span>
                          <a href={`https://orcid.org/${CURRENT_CONFIG.orcidId}`} target="_blank" rel="noreferrer" className="text-xs text-neutral-400 hover:text-amber-400 font-mono transition">
                            {CURRENT_CONFIG.orcidId}
                          </a>
                        </div>
                      </div>

                      <div className="bg-neutral-950 border border-neutral-800/60 rounded-xl p-4">
                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider block mb-1">
                          Zenodo Peer-Reviewed Permanent Records
                        </span>
                        <p className="text-xs text-neutral-300">Certified archival deposits registered on Zenodo:</p>
                        <div className="mt-3 flex flex-col gap-2">
                          <a 
                            href="https://doi.org/10.5281/zenodo.23361838" 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-xs text-amber-400 hover:underline flex items-center justify-between font-mono p-2 rounded bg-neutral-900/60 border border-amber-500/30"
                          >
                            <span className="flex items-center gap-1.5 font-bold">
                              <Info className="w-3.5 h-3.5 text-amber-400" />
                              Preprint DOI: 10.5281/zenodo.23361838
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">Live</span>
                          </a>
                          <a 
                            href="https://doi.org/10.5281/zenodo.23391403" 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-xs text-cyan-400 hover:underline flex items-center justify-between font-mono p-2 rounded bg-neutral-900/60 border border-cyan-500/30"
                          >
                            <span className="flex items-center gap-1.5 font-bold">
                              <Check className="w-3.5 h-3.5 text-cyan-400" />
                              Software DOI: 10.5281/zenodo.23391403
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">Live</span>
                          </a>
                          <span className="text-[9px] text-neutral-500 font-mono mt-0.5">
                            Subdomain Deployment: Cloudflare Edge &bull; Contact: support@bhutadamarasena.com
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {CURRENT_CONFIG.hasLicensingBlock && (
                    <div className="mt-4 bg-neutral-950 border border-neutral-800/40 rounded-xl p-4 text-xs text-neutral-400 leading-relaxed font-mono">
                      <div className="flex items-center gap-2 text-neutral-300 font-bold mb-1">
                        <Check className="w-4 h-4 text-emerald-500" />
                        MIT Open-Source Validation Protocol
                      </div>
                      <p className="text-[11px] text-neutral-500">
                        {CURRENT_CONFIG.licenseNotice}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {/* TAB 5: AI EDGE STREAM */}
        {mainTab === "aistream" && (
          <div className="flex flex-col gap-6 w-full">
            <StreamErrorBoundary>
              <AIStreamDemo />
            </StreamErrorBoundary>
          </div>
        )}

        {/* TAB 6: SETTINGS */}
        {mainTab === "settings" && (
          <div className="flex flex-col gap-6 max-w-4xl mx-auto w-full">
            <SettingsPanel
              settings={settings}
              onSettingsChange={saveSettings}
            />
          </div>
        )}
        </Suspense>
      </main>


      {/* Add Custom Mantra Modal (LocalStorage Persistence) */}
      <CustomMantraModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddMantra={(mantraToAdd) => {
          const updatedMantras = [...mantras, mantraToAdd];
          setMantras(updatedMantras);
          localStorage.setItem("vedic_mantras_store", JSON.stringify(updatedMantras));
          setSelectedMantra(mantraToAdd);
        }}
      />

      {/* Email Report Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 max-w-md w-full shadow-2xl relative"
            >
              <h3 className="text-lg font-bold text-neutral-100 mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-500" /> Email Practice Report
              </h3>
              <p className="text-xs text-neutral-400 mb-5 leading-normal">
                Transmit your sacred chanting and pitch calibration metrics securely via Cloudflare Edge Email to your personal mailbox.
              </p>

              <form onSubmit={handleSendEmailReport} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-neutral-400 block mb-1">Recipient Email Address</label>
                  <input
                    type="email"
                    required
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="e.g. user@example.com"
                    disabled={emailStatus === "sending" || emailStatus === "success"}
                    className="w-full bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-amber-500 disabled:opacity-50"
                  />
                </div>

                {emailStatus === "success" && (
                  <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0 text-emerald-400" />
                    Practice report transmitted successfully!
                  </div>
                )}

                {emailStatus === "error" && (
                  <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-400" />
                    <span>{emailError}</span>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-800">
                  <button
                    type="button"
                    onClick={() => setShowEmailModal(false)}
                    disabled={emailStatus === "sending"}
                    className="text-xs text-neutral-400 hover:text-neutral-200 bg-neutral-800 px-4 py-2 rounded-lg cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={emailStatus === "sending" || emailStatus === "success" || !recipientEmail}
                    className="text-xs bg-amber-500 disabled:opacity-40 text-neutral-950 font-bold px-4 py-2 rounded-lg hover:bg-amber-600 transition flex items-center gap-1.5 cursor-pointer"
                  >
                    {emailStatus === "sending" ? (
                      <span>Transmitting...</span>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Send Report</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Premium Lock Modal */}
      <AnimatePresence>
        {showLockedFeatureModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-950/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl relative"
            >
              <button
                onClick={() => setShowLockedFeatureModal(null)}
                className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-300 font-bold text-xs select-none cursor-pointer"
              >
                ✕
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="p-4 bg-amber-500/10 border border-amber-500/25 rounded-full text-amber-500 mb-4 animate-bounce">
                  <Lock className="w-8 h-8" />
                </div>
                <h4 className="text-base font-bold text-neutral-150 mb-2">
                  {showLockedFeatureModal} is Premium
                </h4>
                <p className="text-xs text-neutral-400 mb-6 leading-relaxed">
                  Advanced phonetics analytics, custom export routines, and high-compute DSP audio synthesis require an Ancient Runes PRO subscription or Lifetime Founder Pass via Stripe.
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => {
                      setShowLockedFeatureModal(null);
                      setIsProHubOpen(true);
                    }}
                    className="flex-1 text-center py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-neutral-950 font-bold rounded-xl text-xs transition cursor-pointer select-none"
                  >
                    Upgrade via Stripe
                  </button>
                  <button
                    onClick={() => setShowLockedFeatureModal(null)}
                    className="flex-1 py-2 bg-stone-800 hover:bg-stone-700 text-neutral-200 font-semibold rounded-xl text-xs transition cursor-pointer select-none"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stripe Self-MoR Legal Footer */}
      <LegalMoRFooter currentSubdomain={window.location.hostname || "bhutadamarasena.com"} />

      <Suspense fallback={null}>
        {/* Pro Features & Stripe Self-MoR Hub Modal */}
        <ProFeaturesHub isOpen={isProHubOpen} onClose={() => setIsProHubOpen(false)} />

        {/* TMD-Hash v12.0 Cryptographic Oracle Modal */}
        <AnimatePresence>
          {isTmdModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl"
              >
                <div className="relative">
                  <button
                    onClick={() => setIsTmdModalOpen(false)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-stone-900/90 text-stone-400 hover:text-stone-100 border border-stone-700 transition"
                  >
                    ✕
                  </button>
                  <TmdHashInspector />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Automata Version 10.0 Trajectory Optimization Modal */}
        <AnimatePresence>
          {isAutomataModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl"
              >
                <div className="relative">
                  <button
                    onClick={() => setIsAutomataModalOpen(false)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-stone-900/90 text-stone-400 hover:text-stone-100 border border-stone-700 transition"
                  >
                    ✕
                  </button>
                  <AutomataStudio />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Ancient Runes Zenodo Monograph V1.0 Modal */}
        <AnimatePresence>
          {isMonographModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl"
              >
                <div className="relative">
                  <button
                    onClick={() => setIsMonographModalOpen(false)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-stone-900/90 text-stone-400 hover:text-stone-100 border border-stone-700 transition cursor-pointer"
                  >
                    ✕
                  </button>
                  <AncientRunesMonographModal />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Finite-Dimensional Modular Theory Version 10.0 Studio Modal */}
        <AnimatePresence>
          {isFiniteDimModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl"
              >
                <div className="relative">
                  <button
                    onClick={() => setIsFiniteDimModalOpen(false)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-stone-900/90 text-stone-400 hover:text-stone-100 border border-stone-700 transition cursor-pointer"
                  >
                    ✕
                  </button>
                  <FiniteDimStudio />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Coupled SPDE Version 6.0 Studio Modal */}
        <AnimatePresence>
          {isSpdeV6ModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl"
              >
                <div className="relative">
                  <button
                    onClick={() => setIsSpdeV6ModalOpen(false)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-stone-900/90 text-stone-400 hover:text-stone-100 border border-stone-700 transition cursor-pointer"
                  >
                    ✕
                  </button>
                  <SpdeV6Studio />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </Suspense>
    </div>
  );
}
